import React from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { Edit } from '@mui/icons-material';
import moment from 'moment';
import './ScheduleStatsBox.scss';

interface ScheduleStatsBoxProps {
	fromDate: string;
	toDate: string;
	startOfWeek?: string;
	sales?: number;
	splh?: number;
	cplh?: number;
	hoursScheduled?: number;
	dailyOT?: number;
	weeklyOT?: number;
	customers?: number;
	onPublish?: () => void;
	onCopy?: () => void;
	onEdit?: () => void;
	canManageShifts?: boolean;
	canPublishShifts?: boolean;
	canEditStats?: boolean;
	hasUnpublishedShifts?: boolean;
}

const ScheduleStatsBox: React.FC<ScheduleStatsBoxProps> = ({
	fromDate,
	toDate: _toDate,
	startOfWeek = 'Monday',
	sales = 0,
	splh = 0,
	cplh = 0,
	hoursScheduled = 0,
	dailyOT = 0,
	weeklyOT = 0,
	customers = 0,
	onPublish,
	onCopy,
	onEdit,
	canManageShifts = false,
	canPublishShifts = false,
	canEditStats = false,
	hasUnpublishedShifts = false,
}) => {
	const formatWeekRange = () => {
		if (!fromDate) return 'N/A';

		// fromDate is already set to the business week start by SchedulePage
		// Just calculate the end date (7 days from start)
		const businessWeekStart = moment(fromDate);
		const businessWeekEnd = businessWeekStart.clone().add(6, 'days');

		return `${businessWeekStart.format('MM/DD/YY')} - ${businessWeekEnd.format('MM/DD/YY')}`;
	};

	const handleEditClick = () => {
		if (onEdit) {
			onEdit();
		}
	};

	return (
		<Paper
			className="schedule-stats-box"
			sx={{ p: 3, mb: 3, backgroundColor: '#fff', borderRadius: 2 }}
		>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
					>
						<Box>
							<Typography
								variant="h6"
								sx={{
									fontFamily: 'Gotham',
									fontWeight: 500,
									color: '#000000',
									margin: 0,
									fontSize: '20px',
									lineHeight: 1.6,
								}}
							>
								Business Week
							</Typography>
							<Typography
								variant="h6"
								sx={{
									fontFamily: 'Gotham',
									fontWeight: 600,
									color: 'rgb(68, 99, 136)',
									marginTop: 0.5,
								}}
							>
								{formatWeekRange()}
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', gap: 2 }}>
							{onCopy && canPublishShifts && (
								<button
									onClick={onCopy}
									style={{
										backgroundColor: 'rgb(68, 99, 136)',
										color: '#fff',
										height: '32px',
										borderRadius: '6px',
										fontSize: '14px',
										fontWeight: '500',
										fontFamily: 'Gotham',
										padding: '0 24px',
										border: 'none',
										cursor: 'pointer',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = 'rgb(57, 80, 111)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = 'rgb(68, 99, 136)';
									}}
								>
									Copy
								</button>
							)}
							{onPublish && canPublishShifts && (
								<button
									onClick={onPublish}
									style={{
										backgroundColor: 'rgb(68, 99, 136)',
										color: '#fff',
										height: '32px',
										borderRadius: '6px',
										fontSize: '14px',
										fontWeight: '500',
										fontFamily: 'Gotham',
										padding: '0 24px',
										border: 'none',
										cursor: hasUnpublishedShifts ? 'pointer' : 'not-allowed',
										opacity: hasUnpublishedShifts ? 1 : 0.5,
									}}
									onMouseEnter={(e) => {
										if (hasUnpublishedShifts) {
											e.currentTarget.style.backgroundColor = 'rgb(57, 80, 111)';
										}
									}}
									onMouseLeave={(e) => {
										if (hasUnpublishedShifts) {
											e.currentTarget.style.backgroundColor = 'rgb(68, 99, 136)';
										}
									}}
									disabled={!hasUnpublishedShifts}
								>
									Publish
								</button>
							)}
						</Box>
					</Box>
					<Box sx={{ overflowX: 'auto' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Gotham' }}>
							<thead>
								<tr style={{ borderBottom: '1px solid #e0e0e0' }}>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										Projected Sales
									</th>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										Projected Customers
									</th>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										SPLH
									</th>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										CPLH
									</th>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										Hours Scheduled
									</th>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										Daily O/T
									</th>
									<th
										style={{
											padding: '12px 8px',
											textAlign: 'center',
											fontWeight: 600,
											fontSize: '14px',
										}}
									>
										Weekly O/T
									</th>
									{canEditStats && (
										<th
											style={{
												width: 48,
												padding: '12px 8px',
												textAlign: 'center',
												fontWeight: 600,
												fontSize: '14px',
											}}
										>
											Edit
										</th>
									)}
								</tr>
							</thead>
							<tbody>
								<tr>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>${sales.toLocaleString()}</span>
									</td>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>{customers.toLocaleString()}</span>
									</td>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>{splh.toFixed(1)}</span>
									</td>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>{cplh.toFixed(1)}</span>
									</td>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>{hoursScheduled.toFixed(1)}</span>
									</td>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>{dailyOT.toFixed(1)}</span>
									</td>
									<td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'center' }}>
										<span>{weeklyOT.toFixed(1)}</span>
									</td>
									{canEditStats && (
										<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
											<button
												style={{
													background: 'none',
													border: 'none',
													cursor: 'pointer',
													padding: '4px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
												}}
												title="Edit"
												onClick={handleEditClick}
											>
												<Edit sx={{ fontSize: 20, color: 'rgb(68, 99, 136)' }} />
											</button>
										</td>
									)}
								</tr>
							</tbody>
						</table>
					</Box>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default ScheduleStatsBox;
